import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { analyticsApi } from '../services/analyticsApi';
import { analyticsCache } from '../utils/analyticsCache';
import Logo from '../components/Logo';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderTrendsData, setOrderTrendsData] = useState<any>({ labels: [], datasets: [] });
  const [revenueGrowthData, setRevenueGrowthData] = useState<any>({ labels: [], datasets: [] });
  const [revenueSummary, setRevenueSummary] = useState({
    yesterday: 0,
    last7days: 0,
    last30days: 0,
    last1year: 0,
  });

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if we have valid cached data
      if (analyticsCache.isValid() && analyticsCache.isForCurrentUser()) {
        const cachedData = analyticsCache.get();
        if (cachedData) {
          console.log('Using cached analytics data');
          setOrderTrendsData(cachedData.orderTrends);
          setRevenueGrowthData(cachedData.revenueGrowth);
          setRevenueSummary(cachedData.revenueSummary);
          setLoading(false);
          return;
        }
      }

      // Fetch fresh data from API
      console.log('Fetching fresh analytics data from API');
      const response = await analyticsApi.getAll();
      
      if (response.status === 'success' && response.data) {
        const { orderTrends, revenueGrowth, revenueSummary: summary } = response.data;
        
        setOrderTrendsData(orderTrends);
        setRevenueGrowthData(revenueGrowth);
        setRevenueSummary(summary);
        
        // Cache the data
        analyticsCache.set({
          orderTrends,
          revenueGrowth,
          revenueSummary: summary,
        });
      } else {
        setError(response.message || 'Failed to load analytics data');
      }
    } catch (err: any) {
      console.error('Analytics loading error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };


  return (
    <Box sx={{ p: 3 }}>

      {/* Order & Revenue Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Order Trends */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Trends
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={orderTrendsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top' },
                      title: { display: false },
                    },
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Growth (Last 1 Year) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Growth (Last 1 Year)
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line
                  data={revenueGrowthData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top' },
                      title: { display: false },
                    },
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Paper sx={{ p: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="h4" sx={{ color: 'primary.contrastText' }}>
                    {formatCurrency(revenueSummary.yesterday)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
                    Yesterday's Revenue
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, backgroundColor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="h4" sx={{ color: 'success.contrastText' }}>
                    {formatCurrency(revenueSummary.last7days)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'success.contrastText' }}>
                    Last 7 Days
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, backgroundColor: 'warning.light', color: 'warning.contrastText' }}>
                  <Typography variant="h4" sx={{ color: 'warning.contrastText' }}>
                    {formatCurrency(revenueSummary.last30days)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'warning.contrastText' }}>
                    Last 30 Days
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, backgroundColor: 'info.light', color: 'info.contrastText' }}>
                  <Typography variant="h4" sx={{ color: 'info.contrastText' }}>
                    {formatCurrency(revenueSummary.last1year)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                    Last 1 Year
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
